import type { QueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import z from "zod";

import { isProjectTypeMember } from "@/core/lib/access";
import { DEPARTMENT_PROJECT_TYPES } from "@/core/lib/project-types";
import { denyRouteAccess } from "@/core/lib/route-access";
import {
  getClassificationTemplateOptions,
  getDocumentAnalysisTemplateOptions,
  getLatestClassificationTemplateVersionOptions,
} from "@/openapi/@tanstack/react-query.gen";

export interface DocumentAnalysisCreateLoaderDeps {
  id?: string;
  promotionRequestId?: string;
  projectType?: (typeof DEPARTMENT_PROJECT_TYPES)[number];
  fromClassificationTemplateId?: string;
  selectedColumn?: string;
  outputFormat?: "text" | "numeric" | "boolean" | "table" | "list" | "date";
  accountGroupCodeColumn?: string;
  accountGroupColumn?: string;
  parameters?: string[];
}

interface DocumentAnalysisCreateLoaderContext {
  queryClient: Pick<QueryClient, "query">;
  user: {
    organization?: {
      id: string;
    } | null;
  };
}

export const Route = createFileRoute("/_protected/document-analysis/create/")({
  validateSearch: z.object({
    id: z.uuid().optional(),
    promotionRequestId: z.uuid().optional(),
    projectType: z.enum(DEPARTMENT_PROJECT_TYPES).optional(),
    fromClassificationTemplateId: z.uuid().optional(),
    selectedColumn: z.string().optional(),
    outputFormat: z
      .enum(["text", "numeric", "boolean", "table", "list", "date"])
      .optional(),
    accountGroupCodeColumn: z.string().optional(),
    accountGroupColumn: z.string().optional(),
    parameters: z.array(z.string()).optional(),
  }),
  loaderDeps: ({
    search: {
      id,
      promotionRequestId,
      projectType,
      fromClassificationTemplateId,
      selectedColumn,
      outputFormat,
      accountGroupCodeColumn,
      accountGroupColumn,
      parameters,
    },
  }) => ({
    id,
    promotionRequestId,
    projectType,
    fromClassificationTemplateId,
    selectedColumn,
    outputFormat,
    accountGroupCodeColumn,
    accountGroupColumn,
    parameters,
  }),
  beforeLoad: ({ context, search, ...guard }) => {
    if (
      search.projectType &&
      !isProjectTypeMember(context.user, search.projectType)
    ) {
      denyRouteAccess(guard, `${search.projectType}_membership`);
    }
  },
  loader: loadDocumentAnalysisCreateRoute,
});

export async function loadDocumentAnalysisCreateRoute({
  context: { queryClient, user },
  deps,
}: {
  context: DocumentAnalysisCreateLoaderContext;
  deps: DocumentAnalysisCreateLoaderDeps;
}) {
  const organizationId = user.organization?.id;

  if (!organizationId) {
    throw redirect({ to: "/no-organization", throw: true });
  }

  if (deps.id) {
    const template = await queryClient.query({
      ...getDocumentAnalysisTemplateOptions({
        path: {
          org_id: organizationId,
          template_id: deps.id,
        },
        query: {
          promotion_request_id: deps.promotionRequestId,
        },
      }),
    });

    if (
      !deps.promotionRequestId &&
      deps.projectType &&
      template.scope.type !== "project" &&
      (template.scope.type !== "project_type" ||
        template.scope.project_type !== deps.projectType)
    ) {
      throw redirect({
        to: "/template-editor",
        search: {
          status: "approved",
          category: "document_analysis",
          projectType: deps.projectType,
        },
      });
    }

    return {
      template,
      classificationTemplateVersion: null,
      selectedColumn: null,
      outputFormat: "text" as const,
      accountGroupCodeColumn: null,
      accountGroupColumn: null,
      parameters: null,
    };
  }

  if (deps.fromClassificationTemplateId && deps.selectedColumn) {
    if (deps.projectType) {
      const classificationTemplate = await queryClient.query({
        ...getClassificationTemplateOptions({
          path: {
            template_id: deps.fromClassificationTemplateId,
            org_id: organizationId,
          },
        }),
      });

      if (classificationTemplate.project_type !== deps.projectType) {
        throw redirect({
          to: "/template-editor",
          search: {
            status: "approved",
            category: "classifications",
            projectType: deps.projectType,
          },
        });
      }
    }

    const classificationTemplateVersion = await queryClient.query({
      ...getLatestClassificationTemplateVersionOptions({
        path: {
          template_id: deps.fromClassificationTemplateId,
          org_id: organizationId,
        },
      }),
    });

    return {
      template: null,
      classificationTemplateVersion,
      selectedColumn: deps.selectedColumn,
      outputFormat: deps.outputFormat ?? "text",
      accountGroupCodeColumn: deps.accountGroupCodeColumn ?? null,
      accountGroupColumn: deps.accountGroupColumn ?? null,
      parameters: deps.parameters ?? null,
    };
  }

  return {
    template: null,
    classificationTemplateVersion: null,
    selectedColumn: null,
    outputFormat: "text",
    accountGroupCodeColumn: null,
    accountGroupColumn: null,
    parameters: null,
  };
}
